import * as Headless from '@headlessui/react';
import { type VariantProps, cva } from 'class-variance-authority';
import { type HTMLMotionProps, motion } from 'motion/react';
import React from 'react';

import { Link } from '@core/components/Link';
import LoadingState from '@core/components/LoadingState';
import { cn } from '@core/utils/styles';

const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center overflow-hidden',
    'rounded-theme outline-offset-2',
    'font-medium',
    'transition-colors',
    'data-focus:outline-2 data-focus:outline-primary dark:data-focus:outline-primary',
    'data-disabled:opacity-60 data-disabled:pointer-events-none',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-primary',
          'text-white',
          'shadow-md inset-shadow-sm inset-shadow-white/20 inset-shadow-sm inset-shadow-white/20',
          'data-hover:bg-primary-700 dark:data-hover:bg-primary-700',
        ],
        interactive: [
          'text-txt-light-primary dark:text-txt-dark-primary',
          'bg-back-light-muted dark:bg-back-dark',
          'border border-primary/20 dark:border-primary/20',
          'inset-ring rounded-theme ring ring-primary-800/20 dark:ring-primary-700/20 dark:inset-ring-primary/20',
          'inset-shadow-sm shadow inset-shadow-primary/50 inset-ring-primary-700/10 dark:inset-shadow-primary-light/50',
          'data-hover:bg-stroke-light dark:data-hover:bg-back-nav-highlight-dark',
        ],
        ghost: [
          'bg-transparent',
          'text-txt-light-primary dark:text-txt-dark-primary',
          'underline',
          'data-hover:bg-back-nav-highlight-light/5',
          'dark:data-hover:bg-back-nav-highlight-dark',
          'dark:data-hover:bg-back-light/10',
        ],
        outline: [
          'border',
          'border-primary-light',
          'bg-transparent',
          'text-primary',
          'data-hover:bg-primary-light/10',
          'data-focus:outline-primary-light',
          'dark:border-primary-lightest',
          'dark:text-primary-300',
          'dark:data-hover:bg-primary-lightest/10',
        ],
        destructive: [
          'bg-red-500',
          'text-white',
          'data-hover:bg-red-600',
          'data-focus:outline-red-500',
          'dark:data-focus:outline-red-500',
          'dark:bg-red-500',
          'dark:data-hover:bg-red-600',
        ],
      },
      size: {
        default: ['py-2 px-4', 'text-sm', 'min-h-9'],
        sm: ['px-3', 'text-xs', 'min-h-8'],
        lg: ['px-8', 'text-base', 'min-h-11'],
        icon: ['size-10', 'p-2'],
        'icon-xs': ['size-6', 'p-1', 'rounded-theme-min'],
        'icon-sm': ['size-8', 'p-1.5'],
        'icon-lg': ['size-12', 'p-2.5'],
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

interface ButtonProps
  extends Omit<
      React.ButtonHTMLAttributes<HTMLAnchorElement | HTMLButtonElement>,
      'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'
    >,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  loadingText?: string;
  href?: string;
  children: React.ReactNode;
  motionProps?: HTMLMotionProps<'button'>;
  ref?: React.Ref<HTMLAnchorElement | HTMLButtonElement>;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  animate?: boolean;
}

export const Button = ({
  ref,
  className,
  variant,
  size,
  isLoading,
  href,
  children,
  disabled,
  type = 'button',
  loadingText,
  animate = false,
  motionProps,
  ...props
}: ButtonProps) => {
  return href ? (
    <Link
      href={href}
      className={cn(buttonVariants({ variant, size }), className)}
      ref={ref as React.RefObject<HTMLAnchorElement> | undefined}
      type={type}
      {...props}
    >
      {children}
    </Link>
  ) : animate || loadingText ? (
    <Headless.Button
      className={cn(buttonVariants({ variant, size }), className)}
      ref={ref}
      disabled={disabled || isLoading}
      type={type}
      as={motion.button}
      layoutId={motionProps?.layoutId}
      initial={motionProps?.initial}
      animate={motionProps?.animate}
      layout="size"
      transition={{ duration: 0.2, ...motionProps?.transition }}
      {...props}
    >
      {isLoading && (
        <motion.div
          className={cn('flex items-center justify-center')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <LoadingState text={loadingText} />
        </motion.div>
      )}

      {!isLoading && (
        <motion.div
          className={cn('relative flex items-center justify-center gap-2')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>
      )}
    </Headless.Button>
  ) : (
    <Headless.Button
      className={cn(buttonVariants({ variant, size }), className)}
      ref={ref}
      disabled={disabled || isLoading}
      type={type}
      {...props}
    >
      {children}
    </Headless.Button>
  );
};

Button.displayName = 'Button';
